import { useEffect, useState } from 'react';
import { fetchTesseractStaffList } from '../services/tesseractStaffApi';

// Minecraft UUID → tesseract team labels, loaded once per session.
let teamsByUuid: Map<string, string[]> | null = null;
let pending: Promise<Map<string, string[]>> | null = null;
const UPDATED_EVENT = 'staffTeamsUpdated';

const normalize = (uuid: string) => uuid.toLowerCase().replace(/-/g, '');

function loadTeams(): Promise<Map<string, string[]>> {
  if (teamsByUuid) return Promise.resolve(teamsByUuid);
  if (!pending) {
    pending = fetchTesseractStaffList()
      .then((entries) => {
        const map = new Map<string, string[]>();
        for (const entry of entries) {
          if (entry?.minecraft_uuid) map.set(normalize(entry.minecraft_uuid), entry.teams ?? []);
        }
        teamsByUuid = map;
        document.dispatchEvent(new Event(UPDATED_EVENT));
        return map;
      })
      .catch(() => {
        teamsByUuid = new Map();
        return teamsByUuid;
      })
      .finally(() => {
        pending = null;
      });
  }
  return pending;
}

/** Tesseract team labels for a Minecraft UUID (empty while loading or when unknown). */
export const useStaffTeams = (uuid: string | null | undefined): string[] => {
  const [teams, setTeams] = useState<string[]>(() => (uuid && teamsByUuid ? teamsByUuid.get(normalize(uuid)) ?? [] : []));

  useEffect(() => {
    if (!uuid) {
      setTeams([]);
      return;
    }
    let cancelled = false;
    loadTeams().then((map) => {
      if (!cancelled) setTeams(map.get(normalize(uuid)) ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [uuid]);

  return teams;
};
